import { RefObject, useEffect, useState } from 'react'

import useIsOnMobile from '../../../services/useIsOnMobile'
import { ScreenSize } from '../../../utils/constants'
import { getRefDimension } from '../../../utils/util'
import { useHeaderRef } from '../../Layouts/Header/hooks/headerContext'

interface UseActiveAttributePanelProps {
  productFeaturesRef: RefObject<HTMLDivElement>
  productStandardsRef: RefObject<HTMLDivElement>
  deliveryTermsRef: RefObject<HTMLDivElement>
  supplierProductsRef?: RefObject<HTMLDivElement>
  navbarRef: RefObject<HTMLDivElement>
  navbarScrollableRef: RefObject<HTMLUListElement>
  isHeaderHidden: boolean
}

type ActiveAttributePanel = 'features' | 'standards' | 'deliveryTerms' | 'supplierCard' | ''

const PADDING_OFFSET = 40 // Offset to trigger active navigation link

const useActiveAttributePanel = (props: UseActiveAttributePanelProps) => {
  const {
    productFeaturesRef,
    productStandardsRef,
    deliveryTermsRef,
    supplierProductsRef,
    navbarRef,
    navbarScrollableRef,
    isHeaderHidden,
  } = props

  const headerRef = useHeaderRef()
  const navbarHeight = getRefDimension('height', navbarRef)
  const headerHeight = !isHeaderHidden ? getRefDimension('height', headerRef) : 0
  const [activeAttributePanel, setActiveAttributePanel] = useState<ActiveAttributePanel>('')
  const isOnMobile = useIsOnMobile(ScreenSize.sm)

  const getSegmentPosition = (position: 'top' | 'bottom', ref?: RefObject<HTMLDivElement>) => {
    const refDimension = getRefDimension(position, ref)
    return refDimension + document.documentElement.scrollTop - headerHeight - navbarHeight
  }

  useEffect(() => {
    const featuresTop = getSegmentPosition('top', productFeaturesRef)
    const standardsTop = getSegmentPosition('top', productStandardsRef)
    const deliveryTermsTop = getSegmentPosition('top', deliveryTermsRef)
    const deliveryTermsBottom = getSegmentPosition('bottom', deliveryTermsRef)
    const supplierCardBottom = getSegmentPosition('bottom', supplierProductsRef)

    // Automatically scroll product navbar horizontally to correct position
    // when the respective attribute panel is active. Only for mobile views.
    const handleHorizontalScroll = (scrollPosition = 0) => {
      navbarScrollableRef.current?.scrollTo({
        top: 0,
        left: scrollPosition,
        behavior: 'smooth',
      })
    }

    const handleActiveAttributePanel = (
      attributePanel: ActiveAttributePanel,
      scrollPosition?: number,
    ) => setTimeout(() => {
      setActiveAttributePanel(attributePanel)
      if (isOnMobile) handleHorizontalScroll(scrollPosition)
    }, 200)

    const handleScrollPositionStyling = () => {
      const currentScrollY = window.scrollY
      const deliveryTermsScrollPosition = 70
      const supplierCardsScrollPosition = navbarScrollableRef?.current?.clientWidth

      if (currentScrollY >= featuresTop - PADDING_OFFSET
        && currentScrollY < standardsTop - PADDING_OFFSET) {
        handleActiveAttributePanel('features')
      } else if (currentScrollY >= standardsTop - PADDING_OFFSET
        && currentScrollY < deliveryTermsTop - PADDING_OFFSET) {
        handleActiveAttributePanel('standards')
      } else if (currentScrollY >= deliveryTermsTop - PADDING_OFFSET
        && currentScrollY < deliveryTermsBottom + PADDING_OFFSET) {
        handleActiveAttributePanel('deliveryTerms', deliveryTermsScrollPosition)
      } else if (currentScrollY >= deliveryTermsBottom + PADDING_OFFSET
        && currentScrollY <= supplierCardBottom) {
        handleActiveAttributePanel('supplierCard', supplierCardsScrollPosition)
      } else {
        setActiveAttributePanel('')
      }
    }

    window.addEventListener('scroll', handleScrollPositionStyling, { passive: true })

    return () => window.removeEventListener('scroll', handleScrollPositionStyling)
  }, [supplierProductsRef?.current, navbarRef, isHeaderHidden])

  return activeAttributePanel
}

export default useActiveAttributePanel
